/// <amd-module name="Wit/SprintsHub/SprintsView/Components/SprintHeader/SprintHeader" />

import { IIterationData, ITeamSettings } from "Agile/Contracts/Common";
import * as React from "react";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableArrayEventArgs, ObservableValue } from "VSS/Core/Observable";
import { IVssComponentContext, IVssLayoutManager, WrappedComponent } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Header, TitleSize } from "VSSUI/Header";
import { IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Observer } from "VSSUI/Observer";
import { WorkItemClassificationNode } from "Wit/Clients/Wit/Contracts/WorkItemTracking";
import { ICurrentDirectoryService } from "Wit/Common/CurrentDirectoryService/ICurrentDirectoryService";
import { FavoriteTypes } from "Wit/Common/Generated/Constants";
import { DirectoryDropdown } from "Wit/DirectoryView/Components/DirectoryDropdown";
import { DirectoryDropdownProvider } from "Wit/DirectoryView/DirectoryDropdownProvider";
import { ISprintsDirectoryService } from "Wit/SprintsHub/Common/ISprintsDirectoryService";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";
import { SprintDates } from "./SprintDates";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { TimerManagement } from "VSS/Core/TimerManagement";

export interface ISprintHeaderProps {
    iterationPath: ObservableValue<string>;
    selectedIteration: ObservableValue<IIterationData | undefined>;
    teamSettings: ITeamSettings;
    headerCommandItems?:  IObservableArrayEventArgs<IHeaderCommandBarItem> | IHeaderCommandBarItem[];
    onSprintDetailsChanged: (node: WorkItemClassificationNode) => void;
}

const teamMRUKey = "Agile/Team";
const projectScopeName = "project";

export class SprintHeader extends TfsComponent<ISprintHeaderProps, {}> {
    private itemProvider: DirectoryDropdownProvider;
    private buttonCount: ObservableValue<number> = new ObservableValue(3);
    private timerManagement: TimerManagement;
    private debouncedButtonCount: () => void;
    private isNewSprintsViewEnabled: boolean;

    constructor(props: ISprintHeaderProps, context: IVssComponentContext) {
        super(props, context);

        const routeValues: { [key: string]: string } = {};
        this.isNewSprintsViewEnabled = isFeatureFlagEnabled(this.context.pageContext, FeatureFlags.NewSprintsBacklogsView, false);
        if (this.projectData?.id) {
            routeValues["project"] = this.projectData.id;
        }

        const directoryHref = routeUrl(this.context.pageContext, "ms.vss-work-web.new-sprints-directory-route", routeValues);
        const directoryService = context.pageContext.getService<ISprintsDirectoryService>("ISprintsDirectoryService");
        this.itemProvider = new DirectoryDropdownProvider({
            directoryService,
            artifactTypeName: Resources.Sprints,
            iconProps: { iconName: "Sprint", ariaHidden: true },
            directoryHref,
            favoriteType: FavoriteTypes.WORK_ITERATION,
            newSprintsViewEnabled: this.isNewSprintsViewEnabled,
        });

        if (this.isNewSprintsViewEnabled) {
            this.timerManagement = new TimerManagement();
            this.debouncedButtonCount = this.timerManagement.debounce(this.updateButtonCount, 300, { trailing: true });
        }
    }

    public componentDidMount() {
        if (this.isNewSprintsViewEnabled) {
            window.addEventListener('resize', this.debouncedButtonCount);
            this.buttonCount.value = 3;
            this.updateButtonCount();
        }
    }

    public componentWillUnmount() {
        if (this.isNewSprintsViewEnabled) {
            window.removeEventListener('resize', this.debouncedButtonCount);
            this.timerManagement.dispose();
        }
    }

    public render(): JSX.Element | null {
        const sprintsViewService = this.context.pageContext.getService<ICurrentDirectoryService>("ICurrentDirectoryService");
        const currentItem = sprintsViewService.getCurrentDirectory().value;

        return this.isNewSprintsViewEnabled ? (
            <Observer
                buttonCount={{
                    observableValue: this.buttonCount
                }}
            >
                {(props) => (
                    <Header
                        buttonCount={props.buttonCount}
                        className="wit-sprints-header-row sprints-view-ff-enabled"
                        contentClassName="wit-sprints-header-content"
                        title={
                            <DirectoryDropdown
                                itemProvider={this.itemProvider}
                                selectedItem={currentItem}
                                onSelect={(event, item) => {
                                    sprintsViewService.setCurrentDirectory(item.data?.directory);
                                    const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
                                    settingsService.setEntries({ [teamMRUKey]: item.data?.directory?.id }, SettingsUserScope.Me, projectScopeName, this.projectData?.id);
                                }}
                            />
                        }
                        commandBarClassName="flex flex-end"
                        commandBarItems={this.props.headerCommandItems as IHeaderCommandBarItem[]}
                        titleSize={props.buttonCount === 3 ? TitleSize.Large : TitleSize.Small}
                    />
                )}
            </Observer>
        ) : (
            <Header
                className="wit-sprints-header-row"
                contentClassName="wit-sprints-header-content"
                title={
                    <DirectoryDropdown
                        itemProvider={this.itemProvider}
                        selectedItem={currentItem}
                        onSelect={(event, item) => {
                            sprintsViewService.setCurrentDirectory(item.data?.directory);
                            const settingsService = this.context.pageContext.getService<ISettingsService>("ISettingsService");
                            settingsService.setEntries({ [teamMRUKey]: item.data?.directory?.id }, SettingsUserScope.Me, projectScopeName, this.projectData?.id);
                        }}
                    />
                }
                commandBarClassName="flex flex-end"
                commandBarItems={this.commandBarItems}
                titleSize={TitleSize.Large}
            />
        );
    }

    private getSprintDatesComponent = () => (
        <div key={"sprint-dates"} className="flex flex-column rhythm-horizontal-4 padding-top-16">
            <SprintDates
                selectedIteration={this.props.selectedIteration}
                teamSettings={this.props.teamSettings}
                onSprintDetailsChanged={this.props.onSprintDetailsChanged}
            />
        </div>
    );

    private getBurndownGraphComponent = () => (
        <div key="burndown-report-div" onClick={this.showLightBox}>
            <Observer
                selectedIteration={this.props.selectedIteration}
                iterationPath={this.props.iterationPath}
            >
                {(props: {
                    selectedIteration?: IIterationData;
                    iterationPath: string;
                }) => (
                    <WrappedComponent
                        key={"burndown-report"}
                        wrappedType={"BurndownReport"}
                        dependencies={["ms.vss-reporting-web.burndown-report-content"]}
                        wrappedProps={{
                            teamId: this.teamData?.id,
                            teamName: this.teamData?.name,
                            projectId: this.projectData?.id,
                            iterationPath: props.iterationPath,
                            iterationStartDate: props.selectedIteration?.startDate,
                            iterationEndDate: props.selectedIteration?.finishDate,
                            isMinimalChart: true,
                            isLightbox: false,
                            lastRefresh: new Date()
                        }}
                    />
                )}
            </Observer>
        </div>
    );

    private showLightBox = () => {
        const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutManager.renderCallout(onDismiss => {
            const lightboxProps = {
                title: "",
                onDismiss: onDismiss,
                className: "lightbox-report-content"
            };

            const reportSettings = {
                teamId: this.teamData?.id,
                teamName: this.teamData?.name,
                projectId: this.projectData?.id,
                iterationPath: this.props.iterationPath.value,
                iterationStartDate: this.props.selectedIteration.value?.startDate,
                iterationEndDate: this.props.selectedIteration.value?.finishDate,
                isLightbox: true,
                lastRefresh: new Date()
            };

            return (
                <WrappedComponent wrappedType={"Lightbox"} dependencies={["ms.vss-reporting.reporting-lightbox"]} {...lightboxProps}>
                    <WrappedComponent
                        wrappedType={"BurndownReport"}
                        dependencies={["ms.vss-reporting-web.burndown-report-content"]}
                        {...reportSettings}
                    ></WrappedComponent>
                </WrappedComponent>
            );
        });
    };

    private commandBarItems: IHeaderCommandBarItem[] = [
        {
            id: "sprint-dates",
            renderButton: this.getSprintDatesComponent
        },
        {
            id: "ax-header-graph",
            renderButton: this.getBurndownGraphComponent
        }
    ];

    private updateButtonCount = () => {
        this.buttonCount.value = window.matchMedia('(max-width: 600px)').matches ? 0 : 3;
    }
}
